import { useState } from 'react'

import { api } from '../api'
import type { JournalPostBody } from '../../contracts/api'
import { haptic } from '../telegram'
import '../screens/journal.css'

export type Resonance = 'yes' | 'partly' | 'no'

const CHIPS: { value: Resonance; label: string }[] = [
  { value: 'yes', label: 'отозвалось' },
  { value: 'partly', label: 'отчасти' },
  { value: 'no', label: 'нет' },
]

interface MarkState {
  resonance: Resonance | null
  note: string
}

/**
 * Что уже отмечено в этой сессии. Экраны вложенные: «Сегодня → период → назад»
 * пересоздаёт отметку, и без памяти чипы каждый раз выглядят нетронутыми, а
 * заметка исчезает. Ключ — тип плюс refKey: ровно то, по чему сервер делает
 * upsert.
 */
const remembered = new Map<string, MarkState>()

/**
 * Отметка в журнал: «отозвалось / отчасти / нет» и необязательная заметка.
 * Ставится под любым выводом, который человек может проверить на себе —
 * карточка дня, пересечение недели, период, разбор оракула.
 *
 * Чип пишется сразу: отметка стоит одного касания, иначе её никто не делает.
 * Заметка убрана под строку и сохраняется отдельной кнопкой — набранный текст
 * нельзя терять на промахе по чипу.
 *
 * Сервер перезаписывает запись целиком (upsert по kind+refKey), поэтому в теле
 * всегда идёт текущая заметка: иначе клик по чипу стёр бы уже написанное.
 */
export function JournalMark({
  kind,
  refKey,
  ref,
  keys,
  initial,
}: {
  kind: JournalPostBody['kind']
  refKey: string
  ref: unknown
  keys?: string[]
  initial?: { resonance: Resonance | null; note: string }
}) {
  const cacheKey = `${kind}:${refKey}`
  const known = remembered.get(cacheKey) ?? initial ?? null

  const [resonance, setResonance] = useState<Resonance | null>(known?.resonance ?? null)
  const [note, setNote] = useState(known?.note ?? '')
  const [saved, setSaved] = useState(known?.note ?? '')
  const [open, setOpen] = useState(Boolean(known?.note))
  const [status, setStatus] = useState<'idle' | 'saving' | 'error'>('idle')

  function send(next: MarkState): void {
    setStatus('saving')
    const body: JournalPostBody = { kind, refKey, ref, keys: keys ?? [], note: next.note }
    if (next.resonance) body.resonance = next.resonance
    api
      .post('/api/journal', body)
      .then(() => {
        remembered.set(cacheKey, next)
        setSaved(next.note)
        setStatus('idle')
      })
      .catch(() => setStatus('error'))
  }

  const trimmed = note.trim()
  const dirty = trimmed !== saved.trim()

  return (
    <div className="jmark">
      <div className="jmark-chips">
        {CHIPS.map((chip) => (
          <button
            type="button"
            key={chip.value}
            className={`chip jmark-chip${resonance === chip.value ? ' on' : ''}`}
            aria-pressed={resonance === chip.value}
            onClick={() => {
              haptic('light')
              setResonance(chip.value)
              send({ resonance: chip.value, note: trimmed })
            }}
          >
            {chip.label}
          </button>
        ))}
        <button
          type="button"
          className="jmark-toggle"
          aria-expanded={open}
          onClick={() => {
            haptic('light')
            setOpen((current) => !current)
          }}
        >
          {open ? 'свернуть' : saved ? 'заметка есть' : 'заметка'}
        </button>
      </div>

      {open && (
        <div className="jmark-note">
          <textarea
            className="field jmark-area"
            rows={3}
            maxLength={500}
            placeholder="Как было на самом деле?"
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
          <div className="jmark-foot">
            <span className="jmark-count">{note.length}/500</span>
            {trimmed.length > 0 && (
              <button
                type="button"
                className="jmark-save"
                disabled={status === 'saving' || !dirty}
                onClick={() => {
                  haptic('medium')
                  send({ resonance, note: trimmed })
                }}
              >
                {status === 'saving' ? 'Сохраняем…' : dirty ? 'Сохранить' : 'Сохранено'}
              </button>
            )}
          </div>
        </div>
      )}

      {status === 'error' && <div className="jmark-error">Не записалось. Попробуй ещё раз</div>}
    </div>
  )
}
